import React, { PropsWithChildren } from 'react';
import { FlexProps } from 'pounceV2';
import useRouter from 'Hooks/useRouter';
import useFeatureToggle from 'Hooks/useFeatureToggle';
import urls from 'Source/urls';
import { PageHeaderData } from './constants';

const CONTENT_CATALOG_PATH_PREFIX = urls.contentCatalog.home();
const EMPTY_HEADER = {
  pageTitle: '',
  pageDescription: '',
  documentationLink: '',
  isSticky: false,
  headerStyleProps: {},
  pageHeaderStyleProps: {},
};

interface IPageHeaderContext {
  title: React.ReactNode;
  description: React.ReactNode;
  link?: string;
  isSticky?: boolean;
  setPageTitle: (title: React.ReactNode) => void;
  setPageDescription: (description: React.ReactNode) => void;
  setPageDocumentationLink: (link: string) => void;
  setPageHeaderSticky: (isSticky?: boolean) => void;
  headerStyles: FlexProps;
  pageHeaderStyles: FlexProps;
}

const PageHeaderContext = React.createContext<IPageHeaderContext>({
  title: '',
  description: '',
  link: '',
  isSticky: false,
  setPageTitle: () => {},
  setPageDescription: () => {},
  setPageDocumentationLink: () => {},
  setPageHeaderSticky: () => {},
  headerStyles: {},
  pageHeaderStyles: {},
});

const PageHeaderProviderV3: React.FC<PropsWithChildren> = ({ children }) => {
  const { location } = useRouter();
  const enableContentCatalog = useFeatureToggle('ENABLE_CONTENT_CATALOG');
  const [title, setTitle] = React.useState<React.ReactNode>('');
  const [description, setDescription] = React.useState<React.ReactNode>('');
  const [link, setLink] = React.useState<string | null>('');
  const [isSticky, setSticky] = React.useState(false);
  const [headerStyles, setHeaderStyles] = React.useState({});
  const [pageHeaderStyles, setPageHeaderStyles] = React.useState({});

  React.useLayoutEffect(() => {
    // Normalize pathname by adding trailing slash if missing
    const normalizedPathname = location.pathname.endsWith('/')
      ? location.pathname
      : `${location.pathname}/`;

    // When content-catalog is disabled, treat its paths as unknown so the header
    // doesn't show "Content Catalog" when the user hits the global 404 for that URL.
    const isContentCatalogPath = normalizedPathname.startsWith(CONTENT_CATALOG_PATH_PREFIX);
    const useFallbackForPath = isContentCatalogPath && !enableContentCatalog;

    const {
      pageTitle,
      pageDescription,
      documentationLink,
      isSticky: sticky,
      headerStyleProps,
      pageHeaderStyleProps,
    } = useFallbackForPath ? EMPTY_HEADER : PageHeaderData[normalizedPathname] || EMPTY_HEADER;

    setTitle(pageTitle);
    setDescription(pageDescription);
    setLink(documentationLink);
    setSticky(sticky);
    setHeaderStyles(headerStyleProps);
    setPageHeaderStyles(pageHeaderStyleProps);
  }, [location.pathname, enableContentCatalog]);

  /**
   * Sets page header title; to be used when
   * title is generated from dynamic data
   */
  const setPageTitle = React.useCallback(
    (t: React.ReactNode) => {
      setTitle(t);
    },
    [setTitle],
  );

  /**
   * Sets page header description
   */
  const setPageDescription = React.useCallback(
    (d: React.ReactNode) => {
      setDescription(d);
    },
    [setDescription],
  );

  /**
   * Sets page header documentation link
   */
  const setPageDocumentationLink = React.useCallback(
    (l?: string) => {
      setLink(l);
    },
    [setLink],
  );

  /**
   * Sets whether the page header should "stick" to the top of the screen.
   */
  const setPageHeaderSticky = React.useCallback(
    (s?: boolean) => {
      setSticky(s);
    },
    [setSticky],
  );

  const contextValue = React.useMemo(
    () => ({
      title,
      description,
      link,
      isSticky,
      headerStyles,
      pageHeaderStyles,
      setPageTitle,
      setPageDescription,
      setPageDocumentationLink,
      setPageHeaderSticky,
    }),
    [
      title,
      description,
      link,
      isSticky,
      headerStyles,
      pageHeaderStyles,
      setPageTitle,
      setPageDescription,
      setPageDocumentationLink,
      setPageHeaderSticky,
    ],
  );

  return <PageHeaderContext.Provider value={contextValue}>{children}</PageHeaderContext.Provider>;
};

const usePageHeaderContextV3 = () => {
  const context = React.useContext(PageHeaderContext);
  if (context === undefined) {
    throw new Error('usePageHeaderContext must be used within a PageHeaderProvider');
  }
  return context as IPageHeaderContext;
};

export { PageHeaderProviderV3, usePageHeaderContextV3 };
